import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Collapse } from "react-bootstrap";
import { getSurveyors } from "../../../../../requesters/userRequester";
import { getJobServiceAsssignedSurveyor } from "../../../../../requesters/jobServiceRequester";
import { toast } from "react-toastify"
import { extractApiErrorMessage, MAIN_TOAST_OPTIONS } from "utils/apiErrors"
import { getBaseUrl } from "../../../reducers";
import DashboardCard from "./DashboardCard";
import GoalSummarySection from "./GoalSummarySection";
import TableForSurveyor from "../SurveyorDashboard/TableForSurveyor";

export default function AdminSurveyorDashboardSection() {
  const baseUrl = useSelector(getBaseUrl);
  const [surveyors, setSurveyors] = useState([]);
  const [selectedSurveyorId, setSelectedSurveyorId] = useState("");
  const [jobServices, setJobServices] = useState([]);
  const [jobCompleted, setJobCompleted] = useState(0);
  const [overdueData, setOverdueData] = useState([]);
  const [upcomingServices, setUpcomingServices] = useState(0);
  const [loadingSurveyors, setLoadingSurveyors] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    getSurveyors(baseUrl)
      .then(res => {
        if (res.status === 200 && res.response) {
          setSurveyors(res.response);
        }
      })
      .finally(() => setLoadingSurveyors(false));
  }, [baseUrl]);

  const requestJobServices = useCallback(() => {
    if (!selectedSurveyorId) return;

    setLoadingJobs(true);
    getJobServiceAsssignedSurveyor(baseUrl, selectedSurveyorId)
      .then(res => {
        if (res.status === 200) {
          const response = res.response?.json ?? res.response;
          const newData = Object.values(response?.daily_jobs?.data ?? response?.daily_jobs ?? {});
          setOverdueData(response?.overdue_jobs ?? []);
          setJobServices(Array.isArray(newData) ? newData : []);
          setJobCompleted(response?.completed_jobs ?? 0);
          setUpcomingServices(response?.service_required_count ?? 0);
        }
      })
      .catch(err => {
        toast.error(`Could not load surveyor dashboard jobs: ${extractApiErrorMessage(err)}`, MAIN_TOAST_OPTIONS)
      })
      .finally(() => setLoadingJobs(false));
  }, [baseUrl, selectedSurveyorId]);

  useEffect(() => {
    if (selectedSurveyorId) {
      requestJobServices();
    } else {
      setJobServices([]);
      setJobCompleted(0);
      setOverdueData([]);
      setUpcomingServices(0);
    }
  }, [selectedSurveyorId, requestJobServices]);

  const selectedSurveyor = surveyors.find(s => String(s[2]) === String(selectedSurveyorId));
  const surveyorDisplayName = selectedSurveyor ? `${selectedSurveyor[0]} ${selectedSurveyor[1]}` : "";

  const syntheticCurrentUser = selectedSurveyorId
    ? { id: selectedSurveyorId, attributes: { firstname: surveyorDisplayName } }
    : null;

  if (loadingSurveyors) return null;

  return (
    <div className="row no-gutters mt-5 pt-4 border-top">
      <div className="col-lg-4 col-xl-3">
        <div className="container-fluid">
          <div className="container-inner">
            <div className="greeting" />
          </div>
        </div>
      </div>
      <div className="col-lg-8 col-xl-9 today">
        <div className="container-fluid">
          <div className="container-inner">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <button
                type="button"
                className="btn btn-link p-0 text-dark font-weight-bold text-decoration-none"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                aria-controls="admin-surveyor-dashboard-collapse"
              >
                <span className={`mr-1 ${isExpanded ? "" : "text-muted"}`}>
                  {isExpanded ? "▼" : "▶"}
                </span>
                {"surveyor dashboard".toUpperCase()}
              </button>
              {surveyorDisplayName && (
                <span className="mx-3 text-muted font-weight-normal">
                  {surveyorDisplayName}
                </span>
              )}
              <div className="d-flex align-items-center">
                <label htmlFor="admin-surveyor-select" className="mb-0 mr-2">
                  View surveyor:
                </label>
                <select
                  id="admin-surveyor-select"
                  className="form-control form-control-sm"
                  style={{ width: "220px" }}
                  value={selectedSurveyorId}
                  onChange={e => setSelectedSurveyorId(e.target.value)}
                >
                  <option value="">Select a surveyor...</option>
                  {[...surveyors]
                    .sort((a, b) => `${a[0]} ${a[1]}`.localeCompare(`${b[0]} ${b[1]}`))
                    .map(([firstname, surname, id]) => (
                      <option key={id} value={id}>
                        {firstname} {surname}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <Collapse in={isExpanded}>
              <div id="admin-surveyor-dashboard-collapse">
                {selectedSurveyorId ? (
                  <>
                    <h2>{"today".toUpperCase()}</h2>
                    <div className="row key-to-do">
                      <div className="col-lg-10">
                        <div className="row">
                          <DashboardCard
                            title="Jobs Completed"
                            total={jobServices.length}
                            ofTotal
                            count={jobCompleted}
                            colorClass="bg-light"
                          />
                          <DashboardCard
                            link={`/flagged-jobs?filter=data_overdue&by_surveyor_flagged=${selectedSurveyorId}`}
                            title="Overdue Data until Today"
                            count={overdueData.length}
                            colorClass="bg-coral"
                          />
                          <DashboardCard
                            link="/settings/gears?by_status=service_required"
                            title="Upcoming Services"
                            count={upcomingServices}
                            colorClass={upcomingServices === 0 ? "bg-light" : "bg-coral"}
                          />
                        </div>
                      </div>
                    </div>
                    <h2 className="mt-4">{"surveyors targets".toUpperCase()}</h2>
                    <div className="row key-to-do">
                      <div className="col-lg-10">
                        <GoalSummarySection surveyorId={parseInt(selectedSurveyorId, 10)} inline />
                      </div>
                    </div>
                    {!!jobServices.length && (
                      <TableForSurveyor
                        currentUser={syntheticCurrentUser}
                        jobServices={jobServices}
                        requestJobServices={requestJobServices}
                      />
                    )}
                  </>
                ) : (
                  <div className="row key-to-do">
                    <div className="col-lg-10">
                      <p className="text-muted">Select a surveyor to view their dashboard.</p>
                    </div>
                  </div>
                )}
              </div>
            </Collapse>
          </div>
        </div>
      </div>
    </div>
  );
}
